import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Zap, MessageCircle, FileText, Users, ShieldCheck } from "lucide-react";
import RegisterModal from "@/components/RegisterModal";
import SignInModal from "@/components/SignInModal";

export default function Landing() {
  const [showRegister, setShowRegister] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-70 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1"
            >
              <Zap className="h-3.5 w-3.5" /> Built for PG, Hostel, Inn & Coliving owners
            </motion.div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Rent eReceipts.<br />
              <span className="text-indigo-600">Delivered to WhatsApp</span> in 10 seconds.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Generate branded, GSTIN-compliant PDF receipts for every tenant. One click sends it
              straight to their WhatsApp — with a shareable download link. Zero setup, zero paperwork.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md"
                onClick={() => setShowRegister(true)}
                data-testid="hero-register-cta"
              >
                Register My PG <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-slate-300 hover:bg-white"
                onClick={() => setShowSignIn(true)}
                data-testid="hero-signin-cta"
              >
                Sign In
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-500" /> JWT-secured owner accounts</span>
              <span className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-indigo-500" /> Cloud-stored PDFs</span>
            </div>
          </div>

          {/* Mock receipt card */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24, rotate: -1 }}
              animate={{ opacity: 1, y: 0, rotate: -1.5 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-indigo-500/10 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display text-xl font-bold">Jashwanth Residency PG</p>
                  <p className="text-xs text-slate-500">Ashok Nagar, Bangalore</p>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest bg-blue-600 text-white px-2 py-1 rounded">Rent Receipt</span>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
                <Field label="Tenant" value="Priya Nair" />
                <Field label="Room" value="102" />
                <Field label="Month" value="Feb 2026" />
                <Field label="Mode" value="UPI" />
                <Field label="Date" value="05 Feb 2026" />
                <Field label="Rcpt #" value="RCT-0142" />
              </div>
              <div className="mt-5 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-slate-700">Amount Paid</span>
                <span className="text-2xl font-display font-bold text-emerald-600">₹ 9,500</span>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <div className="flex-1 h-9 rounded-md bg-slate-100 flex items-center px-3 text-xs text-slate-500 font-mono truncate">/api/receipts/…/pdf</div>
                <div className="h-9 w-9 rounded-md bg-[#25D366] flex items-center justify-center text-white">
                  <MessageCircle className="h-4 w-4" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={<MessageCircle className="h-5 w-5" />} title="1-Click WhatsApp" text="Opens WhatsApp pre-filled with the tenant's number, message and receipt link." accent="#25D366" />
          <Feature icon={<FileText className="h-5 w-5" />} title="Instant PDFs" text="Beautifully formatted, GSTIN-ready receipts generated on the server in seconds." accent="#2563EB" />
          <Feature icon={<Users className="h-5 w-5" />} title="Tenant Directory" text="Add tenants once. Pick from a list every month — room, rent auto-fill." accent="#7C3AED" />
          <Feature icon={<ShieldCheck className="h-5 w-5" />} title="Secure Storage" text="Every receipt is stored in cloud object storage with a shareable link." accent="#059669" />
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-xs text-slate-500 text-center">
          <span>© 2026 PGRentReceipts. All rights reserved.</span>
        </div>
      </footer>

      <RegisterModal open={showRegister} onOpenChange={setShowRegister} />
      <SignInModal open={showSignIn} onOpenChange={setShowSignIn} onSwitchToRegister={() => { setShowSignIn(false); setShowRegister(true); }} />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800 mt-0.5">{value}</p>
    </div>
  );
}

function Feature({ icon, title, text, accent }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-6 hover:shadow-md hover:border-slate-300 transition-all">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white" style={{ background: accent }}>
        {icon}
      </div>
      <h3 className="mt-4 font-display font-semibold text-lg text-slate-900">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
